angular
.module('inkfinder')
.factory('SchedulingFactory', function SchedulingFactory(
  $q,
  ShopHours,
  Appointments,
  MergeProjects,
  Artists,
  ScheduleUtilFactory,
  Schedule,
  moment,
  CalculateAvailability,
  Session,
  _
) {
  // Public methods
  SchedulingFactory.getSchedule = getSchedule;
  SchedulingFactory.fetchScheduleData = fetchScheduleData;
  SchedulingFactory.calculateSchedule = calculateSchedule;
  SchedulingFactory.preprocessAvailabilityLookup = preprocessAvailabilityLookup;

  /**
   * @name getSchedule
   * @description Returns one or more schedules between start and end. If
   * artistId is specified, the schedule data for that artist will be returned
   * if it exists. If no artistId is specified, an array of all schedule data
   * will be returned.
   * @param {unknown} start Unknown
   * @param {unknown} end Unknown
   * @param {unknown} artistId Unknown
   * @return {unknown} Unknown
   */
  function getSchedule(start, end) {
    return $q(function(resolve, reject) {
      fetchScheduleData(start, end).then(function(data) {
        let schedules = calculateSchedule(
          data.rules,
          data.shopHours,
          data.appointments,
          data.artists
        );
        resolve(schedules);
      });
    });
  }

  /**
   * @name fetchScheduleData
   * @description Function to call endpoints needed to return schedule data for
   * the Appointments controller.
   * @param {unknown} start Unknown
   * @param {unknown} end Unknown
   * @param {unknown} request The type of request sent. If request.force = true,
   * then reject any return of data till the forced request is returned.
   * @return {object} scheduleData Returns an object with appointments, artist
   * hours rules, artists, and shop hours.
   */
  function fetchScheduleData(start, end) {
    let search = false;
    let timestamp;
    let scheduleData;
    let artist;
    if (Session.activeState.role === 'artist') {
      artist = Session.collector.id;
    }
    let promise1 = ShopHours.get(Session.activeState.association.shop.id)
    .then(function(hours) {
      let data = {
        mergedData: [],
        shopHours: {}
      };
      let shopHours =
      ScheduleUtilFactory.getShopTimes(hours.all, start, end);
      return Appointments.get(search, shopHours.earliest.start,
      shopHours.latest.end, timestamp, artist)
      .then(function(appointments) {
        // only need appointments that are not archived
        let nonArchivedAppointments = _.filter(appointments, function(appt) {
          return !(appt.current_status == 100);
        });
        if (nonArchivedAppointments.length > 0) {
          // group appointments by artists
          let artistAppointments = _.groupBy(nonArchivedAppointments,
              (appointment) => appointment.artist.id);
          let allApptsSortedByDate = [];
          Object.keys(artistAppointments).forEach(function(key, index) {
            // Find overlapping appointments
            // Sort by start
            let apptsSortedByDate = _.sortBy(artistAppointments[key],
                (appointment) => appointment.start.valueOf());
            // Loop through sorted appts and compare start and end
            // times of consecutive appts
            for (let i = 1; i < apptsSortedByDate.length; ++i) {
              if (apptsSortedByDate[i].start < apptsSortedByDate[i-1].end) {
                apptsSortedByDate[i].overlappingAppt = true;
                apptsSortedByDate[i].apptLevel = i;
                apptsSortedByDate[i-1].overlappingAppt = true;
              }
            }
            allApptsSortedByDate = allApptsSortedByDate.concat(apptsSortedByDate);
          });
          return MergeProjects.merge(allApptsSortedByDate)
              .then(function(mergedData) {
                data = {
                  mergedData: mergedData,
                  shopHours: shopHours
                };
                return data;
              });
        }
        data = {
          mergedData: [],
          shopHours: shopHours
        };
        return data;
      });
    });

    let promise2 = Schedule.fetchSchedules().then(function(rules) {
      return rules;
    });

    let promise3 = Artists.fetchArtists().then(function(artists) {
      if (Session.activeState.role === 'artist') {
        let selectedArtist = _.find(artists.valid, function(artist) {
          if (Session.activeState.association.id === artist.association.id) {
            return true;
          }
        });
        artists.valid = [];
        artists.valid.push(selectedArtist);
        return artists.valid;
      }

      // Find associations with duplicate artists and use the latest one
      let duplicateGroups = _(artists.valid)
        .groupBy(group => group.artist.id)
        .filter(group => group.length > 1)
        .value();

      // Remove all but the latest association
      let newestAssociations = _.map(duplicateGroups, function(group) {
        return _.maxBy(group, 'id');
      });

      // Find all associations that have unque artsts
      let singleGroups = _(artists.valid)
        .groupBy(group => group.artist.id)
        .filter(group => group.length == 1)
        .flatMap(group => group)
        .value();

      // Concat together latest and unique associations
      let updatedArtists = singleGroups.concat(newestAssociations);
      return updatedArtists;
    });
    return $q.all([promise1, promise2, promise3]).then(function(result) {
      scheduleData = {
        shopHours: result[0].shopHours,
        appointments: result[0].mergedData,
        rules: result[1],
        artists: result[2]
      };
      return scheduleData;
    });
  }

  /**
   * @name calculateSchedule
   * @description Function to calculate the schedule based on artist hours,
   * shop hours, appoitments, and artists
   * @param {unknown} rules unknown
   * @param {unknown} shopHours unknown
   * @param {array} appointments The array of projects and appointments
   * @param {array} artists unknown
   * @return {unknown} schedules Unknown
   */
  function calculateSchedule(rules, shopHours, appointments, artists) {
    let schedule = {};
    _.each(artists, function(artist) {
      schedule[artist.id] = {
        artist: artist,
        schedules: [],
        appointments: [],
        artist_start_end_times: []
      };
    });
    let start = shopHours.earliest.start;
    let end = shopHours.latest.end;
    let artistScheduleInstances =
    ScheduleUtilFactory.generateRuleInstances(rules, start, end);
    artistScheduleInstances =
    ScheduleUtilFactory.filterExceptions(artistScheduleInstances);
    //  Add schedule instances to the schedule object, grouped by artist.
    _.each(artistScheduleInstances, function(instance) {
      let artistSelected = instance.rule.artist;
      if (schedule[artistSelected]) {
        schedule[artistSelected].schedules.push(instance);
      }
    });

    for (let j = 0; j < appointments.length; j++) {
      let appointment = appointments[j];
      let status = appointment.appointment.last_status.status_name;
      let date = moment(appointment.appointment.appointment_datetime);
      if (start <= date && date <= end &&
        (status === 'Collector cancelled' || status === 'Artist cancelled'
        || status === 'Requested' || status === 'Unconfirmed'
        || status === 'Confirmed' || status === 'Completed')) {
        let artistForAppointment = appointment.appointment.artist.id;
        if (schedule[artistForAppointment]) {
          schedule[artistForAppointment].appointments.push(appointment);
        }
      }
    }

    let schedules = [];
    for (let key in schedule) {
      if ({}.hasOwnProperty.call(schedule, key)) {
        let artistSchedule = processSchedule(schedule[key], shopHours);
        schedules.push(artistSchedule);
      }
    }
    return schedules;
  }

  /**
   * @name processSchedule
   * @description Function to (I think) build schedule blocks
   * @param {unknown} scheduleGroup Unknown
   * @param {unknown} shopHours Unknown
   * @return {unknown} Unknown Unknown
   */
  function processSchedule(scheduleGroup, shopHours) {
    let start = shopHours.earliest.start;
    let end = shopHours.latest.end;

    let output = {
      artist: scheduleGroup.artist,
      artist_start_end_times: [],
      shop_start_time:
      start.diff(moment(start).startOf('day'), 'minutes'), // Not universally needed
      shop_end_time:
      end.diff(moment(end).startOf('day'), 'minutes'), // Not universally needed
      appointments: [],
      available: []
    };

    let appointments = scheduleGroup.appointments;
    if (appointments) {
      for (let i = 0; i < appointments.length; i++) {
        let project = appointments[i].project;
        let appointment = appointments[i].appointment;
        let newBlock = {
          appointment_buffer: null, //  Do we still need this?
          start: appointment.date,
          end: appointment.end,
          start_time: appointment.date.format('HH:mm:ss'),
          end_time: appointment.end.format('HH:mm:ss'),
          interval_back_to_shop_start_time:
          appointment.date.diff(start, 'minutes'),
          length: appointment.duration, //  Should rename this to duration?
          project: project,
          appointment: appointment
        };
        output.appointments.push(newBlock);
      }
    }

    let scheduleInstances = scheduleGroup.schedules;
    if (scheduleInstances) {
      for (let x = 0; x < scheduleInstances.length; x++) {
        let instance = scheduleInstances[x];
        let rule = instance.rule;

        let interval = {
          start: instance.start,
          end: instance.end,
          start_time: rule.start_time,
          end_time: rule.end_time
        };
        output.artist_start_end_times.push(interval);
      }

      //  Each 'appointment' object is a wrapper for a project and appointment, get the appointments within the wrapper
      let appointmentInner = [];
      for (let j = 0; j < appointments.length; j++) {
        appointmentInner.push(appointments[j].appointment);
      }

      let available = CalculateAvailability.calculate(
        scheduleInstances,
        appointmentInner
      );
      for (let d = 0; d < available.length; d++) {
        let item = available[d];
        item.start_time = item.start.format('HH:mm:ss');
        item.end_time = item.end.format('HH:mm:ss');
        item.length = item.end.diff(item.start, 'minutes'); //  Change to duration?
        item.interval_back_to_shop_start_time =
        item.start.diff(start, 'minutes');
      }
      output.available = available;
    }
    return output;
  }

  /**
   * @name preprocessAvailabilityLookup
   * @description description
   * @param {type} range Description
   * @param {type} defaultSearch Description
   * @return {type} Description
   */
  function preprocessAvailabilityLookup(range, defaultSearch) {
    let start = moment(range.startDate);
    let end;
    if (defaultSearch) {
      end = moment(start).add(1, 'year');
    } else if (range.endDate) {
      end = moment(range.endDate).endOf('day');
    } else {
      end = moment(start).add(1, 'day');
    }
    return {start: start, end: end};
  }
  return SchedulingFactory;
});



// WEBPACK FOOTER //
// ./components/schedule/schedule.service.js